import { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./register.css";
import { auth, db } from "../config/firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import { addDoc, collection } from "firebase/firestore";
import { storage } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useGeoLocation } from "../components/useGeoLocation";
import { geohashForLocation } from "geofire-common";
import { getTownAndCountry } from "../utils/geo";
import { geocode } from "../utils/geocoding";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ClipLoader } from "react-spinners";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";

export const Register = () => {
  // Register component
  const [displayPhoto, setDisplayPhoto] = useState(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const userRef = collection(db, "users");
  const { locationInfo } = useGeoLocation();
  const passwordRules = /^(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).+$/;
  const schema = yup.object().shape({
    displayName: yup.string().required("please enter a display name"),
    email: yup.string().email().required("please enter a valid email address"),
    age: yup
      .number("please specify your age")
      .min(18, "you must be at least 18 years old to register")
      .max(99, "please specify your age")
      .positive("please specify your age")
      .integer("please specify your age")
      .required("please specify your age"),
    location: yup.string().required("please enter your location"),
    password: yup
      .string()
      .min(8, "password must be at least 8 chars")
      .max(20)
      .matches(
        passwordRules,
        "Password must contain at least one number and one special character"
      )
      .required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "passwords do not match")
      .required("password is required"),
    pronouns: yup.string().required("please select your pronouns"),
    sexuality: yup.string().required("please specify your sexuality"),
    role: yup.string().required("please specify your role"),
    lookingFor: yup.array().required("please select at least one option"),
    locationPref: yup.array().required("please select at least one option"),
    bio: yup.string().required("please include your bio"),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (locationInfo) {
      getTownAndCountry(locationInfo.latitude, locationInfo.longitude).then(
        (locationName) => {
          if (locationName) {
            setValue(
              "location",
              `${locationName.town}, ${locationName.country}`
            );
          }
        }
      );
    }
  }, [locationInfo, setValue]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isScriptBlocked, setIsScriptBlocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isAgeVerified, setIsAgeVerified] = useState(false);

  const lookingForList = [
    "relationship",
    "right now",
    "friends",
    "casual",
    "Daddy",
    "leather",
    "kink",
    "fetish",
    "watersports",
    "roleplay",
    "discreet",
    "sauna",
    "public",
    "twink",
    "bear",
    "otter",
    "chub",
    "crossdresser",
    "dating",
    "holding hands",
    "body play",
    "cuddles",
    "kissing",
    "oral",
    "anal",
    "foreplay",
    "bottom",
    "top",
    "versatile",
    "side",
    "boyfriend",
    "trans",
    "chaser",
    "old",
    "young",
    "hairy",
    "smooth",
    "anon",
    "balaclava",
    "glory hole",
    "blindfold",
    "handcuffs",
    "BDSM",
    "conversation",
    "voice call",
    "video call",
    "messaging",
    "nudes",
  ];

  const locationPrefList = ["yours", "mine", "outside", "cafe", "bar", "other"];

  const loadAgeVerifScript = () => {
    return new Promise((resolve, reject) => {
      // check if script already loaded
      if (document.querySelector('script[src*="ageverif.com"]')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      const ageverifKey = process.env.REACT_APP_AGEVERIF_KEY || 'NPhUrAV5jK3S7xdpzfueFsqJkHRvai1WDlmgGIX6';
      script.src = `https://www.ageverif.com/checker.js?key=${ageverifKey}`;
      script.onload = () => resolve();
      script.onerror = () => {
      setIsScriptBlocked(true);
      reject(new Error('Age verification script failed to load.'));
                };
      document.body.appendChild(script);
    });
  };

  
const verifyAge = async () => {
    if (isAgeVerified) return true;
    
    try {
      await loadAgeVerifScript();

      // 1. Set up an event listener to catch when the modal successfully finishes
      window.addEventListener(
        "ageverif:success",
        () => {
          setIsAgeVerified(true);
        },
        { once: true } // This ensures the listener cleans itself up after firing once
      );

      // 2. Start the verification modal
      const result = await window.ageverif.start({ closable: false });
      
      // 3. Fallback: If they were already verified (e.g., from a previous session), 
      // this catches it instantly without needing the event listener.
      if (result && result.verified) {
        setIsAgeVerified(true);
      }

    } catch (err) {
      console.error("Age verification failed:", err);
      setIsScriptBlocked(true);
    }
  };

  // verifyAge replaced by handleAgeVerification
  const createUser = async (newUserData) => {
    setIsLoading(true);
    try {
      // Proceed with account creation (no age verification here)
      const locationData = await geocode(newUserData.location);
      if (!locationData) {
        alert("Could not find that location. Please try again.");
        setIsLoading(false);
        return;
      }

      const { latitude, longitude } = locationData;
      const geohash = geohashForLocation([latitude, longitude], 9);

      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          newUserData.email,
          newUserData.password
        );
      } catch (err) {
        console.error(err);
        if (err.code === "auth/email-already-in-use") {
          alert("This email is already in use.");
        } else {
          alert("Failed to sign up. Please try again.");
        }
        setIsLoading(false);
        return;
      }

      try {
        let url = null;
        if (displayPhoto) {
          const imageRef = ref(
            storage,
            `userMedia/${userCredential.user.uid}/${userCredential.user.uid + displayPhoto.name
            }`
          );
          const snap = await uploadBytes(imageRef, displayPhoto);
          url = await getDownloadURL(snap.ref);
        }

        await addDoc(userRef, {
          ...newUserData,
          isOnline: false,
          isPremium: false,
          isStealth: false,
          isSearchable: true,
          authUID: userCredential.user.uid,
          displayPhoto: url,
          location: { latitude, longitude, geohash },
        });

        await sendEmailVerification(auth.currentUser);
        await signOut(auth);
        setModalOpen(true);
        navigate("/confirmation");
      } catch (err) {
        console.error("Profile setup failed, rolling back user:", err);
        if (auth.currentUser) {
          await auth.currentUser.delete();
        }
        alert("Network error, please try signing up again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // const createUser = async (newUserData) => {
  //     setIsLoading(true);

  //     const loadAgeVerifScript = () => {
  //         return new Promise((resolve, reject) => {
  //             if (document.querySelector('script[src*="ageverif.com"]')) {
  //                 resolve();
  //                 return;
  //             }
  //             const script = document.createElement('script');
  //             script.src = 'https://www.ageverif.com/checker.js?key=NPhUrAV5jK3S7xdpzfueFsqJkHRvai1WDlmgGIX6';
  //             script.onload = () => resolve();
  //             script.onerror = () => {
  //                 setIsScriptBlocked(true);
  //                 reject(new Error('Age verification script failed to load.'));
  //             };
  //             document.body.appendChild(script);
  //         });
  //     };

  //     try {
  //         await loadAgeVerifScript();

  //         if (isScriptBlocked) {
  //             return;
  //         }

  //         const { verified } = await window.ageverif.start({
  //             closable: false
  //         });

  //         if (verified) {
  //             document.querySelectorAll('.blur').forEach(el => el.classList.remove('blur'));

  //             const locationData = await geocode(newUserData.location);
  //             if (!locationData) {
  //                 alert('Could not find that location. Please try again.');
  //                 setIsLoading(false);
  //                 return;
  //             }

  //             const { latitude, longitude } = locationData;
  //             const geohash = geohashForLocation([latitude, longitude], 9);
  //             const userCredential = await createUserWithEmailAndPassword(auth, newUserData.email, newUserData.password);
  //             let url = null;

  //             if (displayPhoto) {
  //                 const imageRef = ref(storage, `userMedia/${userCredential.user.uid}/${userCredential.user.uid + displayPhoto.name}`);
  //                 const snap = await uploadBytes(imageRef, displayPhoto);
  //                 url = await getDownloadURL(snap.ref);
  //             }

  //             await addDoc(userRef, {
  //                 displayName: newUserData.displayName,
  //                 lookingFor: newUserData.lookingFor,
  //                 locationPref: newUserData.locationPref,
  //                 age: newUserData.age,
  //                 role: newUserData.role,
  //                 bio: newUserData.bio,
  //                 pronouns: newUserData.pronouns,
  //                 sexuality: newUserData.sexuality,
  //                 isOnline: false,
  //                 isPremium: true,
  //                 isStealth: false,
  //                 isSearchable: true,
  //                 authUID: userCredential.user.uid,
  //                 displayPhoto: url,
  //                 location: { latitude: latitude, longitude: longitude, geohash }
  //             });

  //             await sendEmailVerification(auth.currentUser);
  //             setModalOpen(true);
  //             navigate('/confirmation');
  //         }

  //     } catch (err) {
  //         console.error(err);
  //         if (err.code === 'auth/email-already-in-use') {
  //             alert('This email address is already in use. Please use a different one or sign in.');
  //         }
  //     } finally {
  //         setIsLoading(false);
  //     }
  // };

  const onSubmit = async (data) => {
    try {
      if (!isAgeVerified) {
        alert("You must complete age verification to continue.");
        return;
      }

      await createUser(data);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="main register">
      <div className="aside">
        <div></div>
      </div>

      <div className="register-modal">
        <Modal
          className="register-success"
          isOpen={modalOpen}
          onRequestClose={() => setModalOpen(false)}
          contentLabel="Successfuly Registered for Flintt"
        >
          <h2>Please check your email for the verification link </h2>
          <button onClick={() => setModalOpen(false)}>Close</button>
        </Modal>
      </div>

      <div className="content has-aside">
        <h1>Sign up to Flintt.me</h1>

        {isScriptBlocked && (
          <div className="error-message" data-testid="ad-blocker-warning">
            <h2>Age Verification Required</h2>
            <p>
              It looks like a script required for age verification has been
              blocked, likely by an ad blocker or browser extension.
            </p>
            <p>
              To continue with registration, please disable any ad blockers for
              this site and refresh the page.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="details-group">
            <span>Enter your email</span>
            <br />
            <p className="error-message">{errors.email?.message}</p>
            <label htmlFor="email" style={{ width: "100%" }}>
              <input
                type="email"
                placeholder="email"
                id="email"
                {...register("email")}
                style={{ width: "100%", minWidth: "0" }}
              />
            </label>
            <p>You can change email notification settings under 'Account'.</p>
          </div>
          <div className="details-group details-group-password">
            <span>
              Choose a password using letters, numbers and special characters of
              at least 8 characters.
            </span>
            <br />
            <p className="error-message">{errors.password?.message}</p>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="password"
                id="password"
                {...register("password")}
                className="password-input password-input-fix"
              />
              <span
                className="password-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <br />
            <span>Confirm password</span>
            <br />
            <p className="error-message">{errors.confirmPassword?.message}</p>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="confirm password"
                id="confirmPassword"
                {...register("confirmPassword")}
                className="password-input password-input-fix"
              />
              <span
                className="password-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div className="details-group">
            <span>Display name</span>
            <br />
            <p className="error-message">{errors.displayName?.message}</p>
            <label htmlFor="displayName" style={{ width: "100%" }}>
              <input
                type="text"
                placeholder="display name"
                id="displayName"
                {...register("displayName")}
                style={{ width: "100%", minWidth: "0" }}
              />
            </label>
            <p>This is what other users will see your name as.</p>
            <p>You can edit your display name later under 'My Profile'.</p>
            <p>We do not recommend using your real name here.</p>
          </div>
          <div className="details-group">
            <span>Age</span>
            <br />
            <p className="error-message">{errors.age?.message}</p>
            <label htmlFor="age" style={{ width: "100%" }}>
              <input
                type="number"
                placeholder="age"
                id="age"
                min="18"
                {...register("age")}
                style={{ width: "100%", minWidth: "0" }}
              />
            </label>
            <p>Enter your current age.</p>
          </div>
          <div className="details-group">
            <span>Location</span>
            <br />
            <p className="error-message">{errors.location?.message}</p>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Add your location (Town, Country)"
                  id="location"
                />
              )}
            />
            <p>This is used to show you profiles nearby.</p>
          </div>
          <div className="details-group">
            <fieldset required>
              <span>Pronouns</span>
              <br />
              <p className="error-message">{errors.pronouns?.message}</p>
              <label htmlFor="pronouns-he">
                <input
                  type="radio"
                  id="pronouns-he"
                  {...register("pronouns")}
                  value="he/him"
                />
                he/him
              </label>
              <label htmlFor="pronouns-she">
                <input
                  type="radio"
                  id="pronouns-she"
                  value="she/her"
                  {...register("pronouns")}
                />
                she/her
              </label>
              <label htmlFor="pronouns-they">
                <input
                  type="radio"
                  id="pronouns-they"
                  value="they/them"
                  {...register("pronouns")}
                />
                they/them
              </label>
              <label htmlFor="pronouns-other">
                <input
                  type="radio"
                  id="pronouns-other"
                  value="other"
                  {...register("pronouns")}
                />
                other
              </label>
              <p>
                Choose the pronouns which others should use to refer to you.
              </p>
            </fieldset>
          </div>
          <div className="details-group">
            <fieldset required>
              <span>Sexuality</span>
              <br />
              <p className="error-message">{errors.sexuality?.message}</p>
              <label htmlFor="sexuality-gay">
                <input
                  type="radio"
                  id="sexuality-gay"
                  value="gay"
                  {...register("sexuality")}
                />
                Gay
              </label>
              <label htmlFor="sexuality-bisexual">
                <input
                  type="radio"
                  id="sexuality-bisexual"
                  value="bisexual"
                  {...register("sexuality")}
                />
                Bisexual
              </label>
              <label htmlFor="sexuality-pansexual">
                <input
                  type="radio"
                  id="sexuality-pansexual"
                  value="pansexual"
                  {...register("sexuality")}
                />
                Pansexual
              </label>
              <label htmlFor="sexuality-other">
                <input
                  type="radio"
                  id="sexuality-other"
                  value="other"
                  {...register("sexuality")}
                />
                other
              </label>
              <p>
                Choosing which sexuality you identify as helps other users to
                discover your profile.
              </p>
            </fieldset>
          </div>
          <div className="details-group">
            <fieldset required>
              <span>Role</span>
              <br />
              <p className="error-message">{errors.role?.message}</p>
              <label htmlFor="role-top">
                <input
                  type="radio"
                  id="role-top"
                  value="top"
                  {...register("role")}
                />
                top
              </label>
              <label htmlFor="role-bottom">
                <input
                  type="radio"
                  id="role-bottom"
                  value="bottom"
                  {...register("role")}
                />
                bottom
              </label>
              <label htmlFor="role-vers">
                <input
                  type="radio"
                  id="role-vers"
                  value="vers"
                  {...register("role")}
                />
                vers
              </label>
              <label htmlFor="role-side">
                <input
                  type="radio"
                  id="role-side"
                  value="side"
                  {...register("role")}
                />
                side
              </label>
              <label htmlFor="role-other">
                <input
                  type="radio"
                  id="role-other"
                  value="other"
                  {...register("role")}
                />
                other
              </label>
              <p>This lets other users know your preferences.</p>
            </fieldset>
          </div>
          <div className="details-group">
            <fieldset required>
              <span>Meet at</span>
              <br />
              <p className="error-message">{errors.locationPref?.message}</p>
              {locationPrefList.map((tag) => (
                <label key={tag} htmlFor={`locationPref-${tag}`}>
                  <input
                    type="checkbox"
                    id={`locationPref-${tag}`}
                    {...register("locationPref")}
                    value={tag}
                  />
                  {tag}
                </label>
              ))}
              <p>Choose your preferred meeting locations.</p>
            </fieldset>
          </div>
          <div className="details-group">
            <fieldset required>
              <span>Looking for</span>
              <br />
              <p className="error-message">{errors.lookingFor?.message}</p>
              {lookingForList.map((tag) => (
                <label key={tag} htmlFor={`lookingFor-${tag}`}>
                  <input
                    type="checkbox"
                    id={`lookingFor-${tag}`}
                    {...register("lookingFor")}
                    value={tag}
                  />
                  {tag}
                </label>
              ))}
              <p>Which types of connections are you looking to make?</p>
            </fieldset>
          </div>
          <div className="almost-done">
            <span>You’re almost done!</span>
            <br />
            <p>
              We recommend uploading at least one profile picture and writing a
              short bio to let other users know about yourself.
            </p>
          </div>
          <div className="details-group">
            <span>Bio</span>
            <br />
            <p className="error-message">{errors.bio?.message}</p>
            <label htmlFor="bio" style={{ width: "100%" }}>
              <textarea
                maxLength="500"
                placeholder="write something about yourself"
                id="bio"
                {...register("bio")}
                style={{ width: "100%", minWidth: "0px" }}
              />
            </label>
            <p>
              This is a space to add likes, dislikes, and a bit of personal
              information about yourself.
            </p>
          </div>
          {/* <div className="details-group">
            <span>Upload an image</span>
            <br />
            <input
              type="file"
              onChange={(e) => setDisplayPhoto(e.target.files[0])}
              accept="image/*"
            />
            <p>
              We recommend using a clear, recent photo of yourself. No nudity on
              display photos, you can add those later.
            </p>
          </div> */}
          <div className="details-group">
            <input type="checkbox" required />
            <span>
              I confirm I agree to the{" "}
              <a href="/terms-of-service">Terms of Service</a> and{" "}
              <a href="/privacy-policy">Privacy Policy</a>.
            </span>
          </div>
          <div className="almost-done">
            {isAgeVerified ? <></> : <></>}
            <span>Next step: age verification.</span>
            <br />
          </div>
          <div className="almost-done">
            <span>Age Verification Required</span>
            <br />
            {isAgeVerified ? (
              <div
                style={{
                  marginTop: "5px",
                  color: "#1a8f32",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: 600,
                  fontFamily: "Aileron-Light",
                  fontVariantLigatures: "no-common-ligatures",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1a8f32"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Age verification completed
              </div>
            ) : (
              <button
                type="button"
                id="verify-age-Button"
                className="verify-age-btn"
                style={{ marginTop: "5px" }}
                onClick={verifyAge}
              >
                Verify Your Age
              </button>

            )}
          </div>

          <div className="submit-button">
            <button type="submit" disabled={isLoading}>
              {isLoading ? <ClipLoader size={20} /> : "Sign up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
